import clsx from "clsx";
import { Tooltip } from "flowbite-react";
import * as React from "react";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GenericComponentTypes } from "@/types/common/library";
import { Combobox, Transition } from "@headlessui/react";
import ComboBoxDisplay, { SelectOption } from "./ComboBoxDisplay";
import Icon, { ICON_SIZES, ICON_TYPE } from "./Icon";

type SelectProps = {
  options: SelectOption[];
  label?: string;
  tooltipText?: string;
  isMulti?: boolean;
  helperText?: string;
  placeHolder?: string;
  disabled?: boolean;
  value?: string | SelectOption[];
  id?: string;
  name?: string;
  onChange?: Function;
  onBlur?: Function;
  required?: boolean;
  error?: boolean;
  warning?: boolean;
  allowCustomValue?: boolean;
} & GenericComponentTypes;

const Select: React.FC<SelectProps> = ({
  label,
  tooltipText,
  options,
  isMulti,
  helperText,
  placeHolder,
  disabled,
  value,
  id,
  name,
  required,
  ["data-testid"]: dataTestId,
  error,
  warning,
  onChange,
  onBlur,
  allowCustomValue,
}) => {
  const { t: ct } = useTranslation("common");

  const [selectedOptions, setSelectedOptions] = useState(
    isMulti
      ? (value as SelectOption[]) || ([] as SelectOption[])
      : (value && options.find((o) => o.value === value)) ||
          ({} as SelectOption)
  );
  const [query, setQuery] = useState("");
  const comboboxButtonRef = React.useRef<HTMLButtonElement>(null);

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) =>
          option.label
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  useEffect(() => {
    setSelectedOptions(
      isMulti
        ? (value as SelectOption[]) || ([] as SelectOption[])
        : (value && options.find((o) => o.value === value)) ||
            ({} as SelectOption)
    );
  }, [value]);

  const onSelectChange = (e: SelectOption) => {
    if (isMulti) {
      const oldSelected = selectedOptions as SelectOption[];
      const found = oldSelected.find((option) => e.value === option.value);
      if (!found) {
        const newValues = [e, ...oldSelected] as SelectOption[];

        setSelectedOptions(newValues);

        var event = {
          target: { name, value: newValues },
        } as unknown as React.SyntheticEvent;
        onChange && onChange(event);
      } else {
        var newSelections = selectedOptions as SelectOption[];
        const index = newSelections.findIndex(
          (option) => option.value === e.value
        );
        newSelections.splice(index, 1);
        setSelectedOptions(newSelections);

        var event = {
          target: { name, value: newSelections },
        } as unknown as React.SyntheticEvent;
        onChange && onChange(event);
      }
    } else {
      let newValue = e as SelectOption;

      if (newValue.value === (selectedOptions as SelectOption).value) {
        newValue = {} as SelectOption;
      }

      setSelectedOptions(newValue);
      var event = {
        target: { name, value: newValue.value || "" },
      } as unknown as React.SyntheticEvent;
      onChange && onChange(event);
    }
  };

  const checkIfSelected = (selected: boolean, option: SelectOption) => {
    return isMulti
      ? !!(selectedOptions as SelectOption[]).find(
          (selectedOption) => selectedOption.value === option.value
        )
      : selected;
  };

  const removeSelection = (value: string) => {
    var newSelections = selectedOptions as SelectOption[];
    const index = newSelections.findIndex((option) => option.value === value);
    newSelections.splice(index, 1);
    setSelectedOptions(newSelections);
    var event = {
      target: { name, value: newSelections },
    } as unknown as React.SyntheticEvent;
    onChange && onChange(event);
  };

  const handleFocusEvent = (e: React.FocusEvent<HTMLInputElement>) => {
    // TODO: FRON-685 Find Better Way to support Blur Event in Lux Select
    if (!e.relatedTarget?.id.startsWith("headlessui-combobox-option")) {
      var event = e;
      event.target.name = name || "";
      onBlur && onBlur(event);
    }
  };

  return (
    <>
      <Combobox
        value={selectedOptions}
        onChange={onSelectChange}
        multiple={isMulti ? false : undefined}
        disabled={disabled}
        name={id}
      >
        {label && tooltipText && (
          <Tooltip content={tooltipText}>
            <Combobox.Label
              className={clsx(
                "mb-[6px]",
                "block",
                "text-sm",
                "font-medium",
                "text-800"
              )}
            >
              {label + (required ? " *" : "")}
              <Icon icon="circle-question" className="ml-2" />
            </Combobox.Label>
          </Tooltip>
        )}
        {label && !tooltipText && (
          <Combobox.Label
            className={clsx(
              "mb-[6px]",
              "block",
              "text-sm",
              "font-medium",
              "text-800"
            )}
          >
            {label + (required ? " *" : "")}
          </Combobox.Label>
        )}
        <div className={clsx("relative")} onBlur={handleFocusEvent}>
          <div
            className={clsx(
              "relative",
              "w-full",
              "cursor-default",
              "overflow-hidden",
              "text-left",
              "[&>div]:!bg-white",
              "dark:[&>div]:!bg-gray_comp",
              "dark:[&>div]:border-gray-600",
              "[&>div>span]:leading-6",
              "[&>div>input]:leading-6",
              "[&>div>input]:py-0",
              "focus:outline-none",
              "focus-visible:ring-2",
              "focus-visible:ring-white",
              "dark:focus-visible:ring-gray-600",
              "focus-visible:ring-opacity-75",
              "focus-visible:ring-offset-2",
              "focus-visible:ring-offset-white",
              "dark:focus-visible:ring-offset-gray-600",
              "sm:text-sm",
              {
                "dark:[&>div]:text-600 dark:[&>div]:border-gray-800": disabled,
              }
            )}
            onClick={() => {
              comboboxButtonRef.current?.click();
            }}
          >
            <Combobox.Input
              as={ComboBoxDisplay}
              selectedOptions={selectedOptions}
              placeHolder={placeHolder || "Search..."}
              isMulti={isMulti}
              query={query}
              error={error}
              warning={warning}
              value={
                isMulti
                  ? (value as SelectOption[])
                  : options.find((o) => o.value === value)
              }
              removeSelection={removeSelection}
              setQuery={setQuery}
            />
            <Combobox.Button
              className={clsx(
                "absolute",
                "inset-y-0",
                "right-0",
                "flex",
                "items-center pr-2",
                {
                  "dark:text-gray_comp": disabled,
                }
              )}
              ref={comboboxButtonRef}
            >
              <Icon icon={"angle-down"} size={ICON_SIZES.BASE} />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              className={clsx(
                "absolute",
                "mt-1",
                "max-h-60",
                "w-full",
                "space-y-0.5",
                "overflow-auto",
                "rounded-md",
                "bg-panel",
                "dark:bg-gray-800",
                "z-[100]",
                "py-1",
                "text-base",
                "shadow-lg",
                "ring-1",
                "ring-black",
                "dark:ring-gray-600",
                "ring-opacity-5",
                "focus:outline-none",
                "sm:text-sm"
              )}
            >
              {filteredOptions.length === 0 && query !== "" ? (
                !!allowCustomValue && query.length > 0 ? (
                  <Combobox.Option
                    className={({ selected, active }) =>
                      clsx(
                        "relative",
                        "cursor-default",
                        "select-none",
                        "rounded-lg",
                        "py-2",
                        "pl-10",
                        "pr-4",
                        active
                          ? "bg-gray-400 text-white dark:bg-gray-700"
                          : checkIfSelected(selected, {
                              value: query,
                              label: query,
                            })
                          ? "bg-[#CDD5E0] text-[#677489] dark:bg-gray-600 dark:text-gray-200"
                          : "text-800"
                      )
                    }
                    value={{
                      value: query,
                      label: query,
                    }}
                  >
                    &quot;{query}&quot;
                  </Combobox.Option>
                ) : (
                  <div
                    className={clsx(
                      "relative",
                      "cursor-default",
                      "select-none",
                      "py-2",
                      "px-4",
                      "text-800"
                    )}
                  >
                    {ct("nothingFound")}
                  </div>
                )
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.value}
                    className={({ selected, active }) =>
                      clsx(
                        "relative",
                        "cursor-default",
                        "select-none",
                        "rounded-lg",
                        "py-2",
                        "pl-10",
                        "pr-4",
                        active
                          ? "bg-gray-400 text-white dark:bg-gray-700"
                          : checkIfSelected(selected, option)
                          ? "bg-[#CDD5E0] text-[#677489] dark:bg-gray-600 dark:text-gray-200"
                          : "text-800"
                      )
                    }
                    value={option}
                  >
                    {({ selected, active }) => {
                      const isSelected = checkIfSelected(selected, option);
                      return (
                        <>
                          <span
                            className={clsx(
                              "block",
                              "truncate",
                              selected ? "font-medium" : "font-normal"
                            )}
                          >
                            {option.icon && (
                              <Icon
                                icon={option.icon}
                                size={ICON_SIZES.LG}
                                className="mr-2"
                              />
                            )}
                            {option.label}
                          </span>
                          {isSelected ? (
                            <span
                              className={clsx(
                                "absolute",
                                "inset-y-0",
                                "left-0",
                                "flex",
                                "items-center",
                                "pl-3",
                                { "text-600": active }
                              )}
                            >
                              <Icon
                                icon={"circle-check"}
                                type={ICON_TYPE.SOLID}
                                size={ICON_SIZES.LG}
                              />
                            </span>
                          ) : null}
                        </>
                      );
                    }}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
      {helperText && (
        <p
          className={clsx(
            "flex",
            "py-2",
            "text-xs",
            error
              ? "text-[#db271b]"
              : warning
              ? "text-[#f1b825]"
              : "text-gray-500"
          )}
        >
          {helperText}
        </p>
      )}
    </>
  );
};

export default Select;
